import _ from 'lodash'
import { Button } from 'primereact/button'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { classNames } from 'primereact/utils'
import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  Address,
  Combobox,
  GenericComboDTO,
  initialStatePhoneDTO,
  InputMails,
  InputPhone,
  InputTextCustom,
  PhoneDTO,
  PhoneNumber,
  getPhoneNumber,
} from 'medere-ui-framework'
import { ComboDataDTO } from '../../../dto/ComboDataDTO'
import CityLocationCombo from '../../../components/CityLocationCombo'
import { useHealthEntityService } from '../../../servicesHooks/HealthEntity/useHealthEntityService'

type PhoneError = Partial<Record<keyof PhoneDTO, boolean>>
type TextChangeEvent = { target: { value: string } }
const DEFAULT_PHONE_TYPE_ID = 2
const DEFAULT_PHONE_TYPE_DESC = 'CEL'

function normalizeEmails(value: string | null | undefined): string {
  return String(value ?? '')
    .split(';')
    .map((mail) => mail.trim())
    .filter((mail) => mail !== '')
    .join(';')
}

interface HealthEntityAddressFormProps {
  newAddress: Address
  setNewAddress: React.Dispatch<React.SetStateAction<Address>>
  newAddressTypes: GenericComboDTO[]
  onConfirm: (createAnother?: boolean, addressToAdd?: Address) => void
  onCancel: () => void
}

export default function HealthEntityAddressForm({
  newAddress,
  setNewAddress,
  newAddressTypes,
  onConfirm,
  onCancel,
}: Readonly<HealthEntityAddressFormProps>) {
  const intl = useIntl()
  const { getHealthEntityPhoneTypes } = useHealthEntityService()
  const [selectedAddressTypes, setSelectedAddressTypes] = useState<GenericComboDTO[]>([])
  const [errorType, setErrorType] = useState(false)
  const [errorDescription, setErrorDescription] = useState(false)
  const [errorEmail, setErrorEmail] = useState(false)
  const [errorCity, setErrorCity] = useState(false)
  const [errorPhones, _setErrorPhones] = useState<PhoneError[]>([])

  useEffect(() => {
    setSelectedAddressTypes(newAddress.types.length > 0 ? newAddress.types : [])
  }, [newAddress])

  useEffect(() => {
    _setErrorPhones(newAddress.phones.map(() => ({ type: false, phonenumber: false })))
  }, [newAddress.phones.length])

  useEffect(() => {
    const normalizedPhones = newAddress.phones.map((phone) => {
      const hasTypeId = Number(phone?.type ?? -1) > 0
      const hasTypeDescription = String(phone?.typedesc ?? '').trim() !== ''
      if (hasTypeId || hasTypeDescription) return phone
      return {
        ...phone,
        type: DEFAULT_PHONE_TYPE_ID,
        typedesc: DEFAULT_PHONE_TYPE_DESC,
      }
    })

    const changed = normalizedPhones.some((phone, index) => (
      phone.type !== newAddress.phones[index]?.type
      || phone.typedesc !== newAddress.phones[index]?.typedesc
    ))

    if (!changed) return

    setNewAddress((prev) => ({
      ...prev,
      phones: normalizedPhones,
    }))
  }, [newAddress.phones, setNewAddress])

  function setErrorPhones(error: PhoneError, index: number) {
    const _errors = [...errorPhones]
    _errors[index] = error
    _setErrorPhones(_errors)
  }

  function confirmAddress(createAnother?: boolean) {
    const resolvedAddress = { ...newAddress }
    let hasErrors = false
    const _errorPhones = [...errorPhones]

    resolvedAddress.phones.forEach((phone: PhoneDTO, index: number) => {
      const hasPartialPhone =
        !_.isEqual(phone.phonenumber, initialStatePhoneDTO.phonenumber) &&
        (phone.phonenumber.number === '' || phone.phonenumber.prefix === '' || phone.typedesc === '')

      if (hasPartialPhone) {
        hasErrors = true
        _errorPhones[index] = {
          type: phone.typedesc === '',
          phonenumber: phone.phonenumber.number === '' || phone.phonenumber.prefix === '',
        }
      }
    })
    _setErrorPhones(_errorPhones)

    if (!selectedAddressTypes.length || !resolvedAddress.addressdesc || !resolvedAddress.city || resolvedAddress.city < 0) {
      if (!selectedAddressTypes.length) setErrorType(true)
      if (!resolvedAddress.addressdesc) setErrorDescription(true)
      if (!resolvedAddress.city || resolvedAddress.city < 0) setErrorCity(true)
      hasErrors = true
    }

    if (hasErrors) return

    setNewAddress(resolvedAddress)
    onConfirm(createAnother, resolvedAddress)
    setSelectedAddressTypes([])
  }

  return (
    <div className='flex flex-column w-full health-entity-address-form'>
      <div className='flex flex-column align-items-start health-entity-address-form-content' >
        <span className={classNames(`p-float-label w-full ${errorType && 'p-invalid'}`, { 'p-empty': newAddress.types.length === 0 })}>
          <MultiSelect
            className='inputContainer w-full'
            value={selectedAddressTypes}
            options={newAddressTypes}
            display='chip'
            optionLabel='description'
            onChange={(e: MultiSelectChangeEvent) => {
              setErrorType(false)
              setSelectedAddressTypes(e.value)
              setNewAddress({ ...newAddress, types: e.value })
            }}
          />
          <label className='label inputLabel' htmlFor='multiselect'>Selecciona el/los tipos de dirección</label>
        </span>

        <InputTextCustom
          label='Dirección'
          floatLabel
          error={errorDescription}
          value={newAddress.addressdesc}
          onChange={(e: TextChangeEvent) => {
            setErrorDescription(false)
            setNewAddress({ ...newAddress, addressdesc: e.target.value })
          }}
        />

        <CityLocationCombo
          value={newAddress.city > 0 || newAddress.citydesc ? {
            id: newAddress.city > 0 ? newAddress.city : -1,
            name: newAddress.citydesc ?? '',
          } : null}
          label={intl.formatMessage({ id: 'City' })}
          error={errorCity}
          onChange={(city: ComboDataDTO | null) => {
            setErrorCity(false)
            setNewAddress({
              ...newAddress,
              city: city?.id ?? -1,
              citydesc: city?.name ?? '',
              cityExternalId: city?.isExternalId ?? false,
            } as Address & { cityExternalId?: boolean })
          }}
        />

        <InputTextCustom
          label='Código postal'
          floatLabel
          value={newAddress.postalcode}
          onChange={(e: TextChangeEvent) => setNewAddress({ ...newAddress, postalcode: e.target.value })}
        />

        {newAddress.phones.map((phone: PhoneDTO, index: number) => (
          <div
            key={index}
            className={`flex gap-1 align-items-baseline w-full health-entity-phone-row ${index === newAddress.phones.length - 1 ? 'mb-4' : 'mb-1'}`.trim()}
          >
            <Combobox
              getItems={getHealthEntityPhoneTypes}
              value={phone.typedesc}
              placeholder='Tipo'
              className='w-3 m-0 health-entity-phone-type-cbo'
              optionLabel='name'
              error={errorPhones[index]?.type}
              setValue={(item: ComboDataDTO | null) => {
                const _phones = [...newAddress.phones]
                _phones[index] = {
                  ..._phones[index],
                  type: item?.id ?? -1,
                  typedesc: item?.name ?? '',
                }
                setNewAddress({ ...newAddress, phones: _phones })
                setErrorPhones({ ...errorPhones[index], type: false }, index)
              }}
            />

            <InputPhone
              className='flex-grow-1 m-0 w-full health-entity-phone-input-inline'
              label={intl.formatMessage({ id: 'Mobile' })}
              value={phone.phonenumber}
              floatLabel
              error={errorPhones[index]?.phonenumber}
              style={{ width: '100%', margin: 0 }}
              setValue={(phoneNumber: PhoneNumber) => {
                setErrorPhones({ ...errorPhones[index], phonenumber: false }, index)
                const _phones = [...newAddress.phones]
                _phones[index] = { ..._phones[index], phonenumber: phoneNumber }
                setNewAddress({ ...newAddress, phones: _phones })
              }}
            />

            {index === newAddress.phones.length - 1 && (
              <Button
                className='buttonSecondary2 p-button-rounded health-entity-add-phone-btn'
                icon='pi pi-plus-circle text-2xl'
                onClick={() => {
                  setNewAddress({
                    ...newAddress,
                    phones: [
                      ...newAddress.phones,
                      {
                        ...initialStatePhoneDTO,
                        type: DEFAULT_PHONE_TYPE_ID,
                        typedesc: DEFAULT_PHONE_TYPE_DESC,
                      },
                    ],
                  })
                  _setErrorPhones([...errorPhones, { type: false, phonenumber: false }])
                }}
              />
            )}
          </div>
        ))}

        <InputMails
          value={newAddress.email}
          onChange={(value: string) => {
            setErrorEmail(false)
            setNewAddress({ ...newAddress, email: normalizeEmails(value) })
          }}
          error={errorEmail}
          className='w-full'
        />

        <div className='flex flex-column w-full health-entity-address-actions' style={{ marginLeft:'-0.6rem' }} >
          <Button
            className='buttonMain3 w-12'
            label='Confirmar y cargar otro domicilio'
            onClick={() => confirmAddress(true)}
          />
          <Button
            disabled={newAddressTypes.length === 0}
            className='buttonMain w-full'
            label='Confirmar domicilio'
            onClick={() => confirmAddress()}
          />
          <Button
            label='Cancelar'
            className='buttonMain2 w-full'
            onClick={onCancel}
          />
        </div>
      </div>
    </div>
  )
}
